// ============================================================================
// AdminPagesPanel — author + manage CMS pages (Task #13).
// ============================================================================
// Sidebar entry: #admin/pages (list view) and #admin/pages/<id> (edit).
// UIF only.
//
// List view: table with title, slug, status, nav order, last updated.
// Edit view: form with slug, title, status (draft/published toggle), layout
// selector, hero fields, SEO meta, nav order, body_md textarea.
//
// Body is plain markdown — same renderer pattern used elsewhere in the app.
// Authors get a live preview pane next to the editor.
// ============================================================================

import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Save, Edit2, Archive, AlertCircle, RefreshCw, FileText, Eye, Send, Globe, EyeOff } from 'lucide-react';
import { api, type CmsPage, type CmsPageInput, type CmsPageStatus, type CmsPageLayout } from '../api';

interface Props {
  token: string;
  // When set, panel renders the edit form for this page_id; otherwise the list.
  editingPageId?: number | 'new' | null;
}

export function AdminPagesPanel({ token, editingPageId }: Props) {
  if (editingPageId === 'new') {
    return <PageEditor token={token} pageId={null} />;
  }
  if (typeof editingPageId === 'number') {
    return <PageEditor token={token} pageId={editingPageId} />;
  }
  return <PagesList token={token} />;
}

// ----------------------------------------------------------------------------
// LIST view
// ----------------------------------------------------------------------------
function PagesList({ token }: { token: string }) {
  const [pages, setPages] = useState<CmsPage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(() => {
    setLoading(true);
    setError('');
    api.adminPages(token)
      .then((d) => setPages(d.pages ?? []))
      .catch((err) => setError(err?.message || 'Could not load pages.'))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  async function archive(p: CmsPage) {
    if (!confirm(`Archive "${p.title}"? It will be hidden publicly and from new edits, but stays in the database.`)) return;
    try {
      await api.archivePage(token, p.page_id);
      refresh();
    } catch (err: any) {
      setError(err?.message || 'Could not archive.');
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pages</h1>
          <p className="text-sm text-gray-600 mt-0.5">
            Marketing pages for upinfarms.com. Edit in markdown, preview live, publish when ready. Drafts are invisible to the public.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={'h-4 w-4 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <a
            href="#admin/pages/new"
            className="inline-flex items-center gap-1.5 rounded-md bg-uif-green text-white font-semibold px-3 py-2 text-sm hover:bg-uif-green-dark"
          >
            <Plus className="h-4 w-4" />
            New page
          </a>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
            <tr>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Slug</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Layout</th>
              <th className="px-3 py-2">Nav</th>
              <th className="px-3 py-2">Updated</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-gray-500 text-center"><Loader2 className="inline h-4 w-4 animate-spin mr-1" />Loading…</td></tr>
            )}
            {!loading && pages.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-gray-500 italic text-center">
                No pages yet. Click "New page" to draft your first.
              </td></tr>
            )}
            {pages.map((p) => (
              <tr key={p.page_id} className={'hover:bg-gray-50 ' + (p.is_archived ? 'opacity-50' : '')}>
                <td className="px-3 py-2 font-medium text-gray-900">
                  <a href={`#admin/pages/${p.page_id}`} className="text-uif-green-dark hover:underline">
                    {p.title}
                  </a>
                  {p.is_archived && <span className="ml-2 text-[10px] uppercase tracking-wider text-gray-500">archived</span>}
                </td>
                <td className="px-3 py-2 text-gray-700"><code className="text-xs">/{p.slug}</code></td>
                <td className="px-3 py-2">
                  <StatusPill status={p.status} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-500 capitalize">{p.layout}</td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {p.nav_order != null ? `#${p.nav_order} ${p.nav_label || ''}` : <em className="text-gray-400">not in nav</em>}
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {p.updated_at ? new Date(p.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  <a
                    href={`#admin/pages/${p.page_id}`}
                    className="inline-block p-1.5 rounded hover:bg-gray-100 text-gray-600"
                    title="Edit"
                  >
                    <Edit2 className="h-4 w-4" />
                  </a>
                  {!p.is_archived && (
                    <button
                      type="button"
                      onClick={() => archive(p)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                      title="Archive"
                      aria-label={`Archive ${p.title}`}
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: CmsPageStatus }) {
  const cls = status === 'published'
    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
    : 'bg-amber-50 text-amber-800 border-amber-200';
  const Icon = status === 'published' ? Globe : EyeOff;
  return (
    <span className={'inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full border ' + cls}>
      <Icon className="h-3 w-3" />
      {status}
    </span>
  );
}

// ----------------------------------------------------------------------------
// EDIT view — new or existing
// ----------------------------------------------------------------------------
function PageEditor({ token, pageId }: { token: string; pageId: number | null }) {
  const isNew = pageId === null;
  const [form, setForm] = useState<CmsPageInput>({
    slug: '',
    title: '',
    status: 'draft',
    layout: 'standard',
    body_md: '',
    hero_image_url: '',
    hero_eyebrow: '',
    seo_title: '',
    seo_description: '',
    og_image_url: '',
    nav_order: null,
    nav_label: '',
  });
  const [original, setOriginal] = useState<CmsPage | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const [showPreview, setShowPreview] = useState(false);

  // Load existing page
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    api.adminPage(token, pageId!)
      .then((d) => {
        if (cancelled) return;
        setOriginal(d.page);
        setForm({
          slug: d.page.slug,
          title: d.page.title,
          status: d.page.status,
          layout: d.page.layout,
          body_md: d.page.body_md,
          hero_image_url: d.page.hero_image_url ?? '',
          hero_eyebrow: d.page.hero_eyebrow ?? '',
          seo_title: d.page.seo_title ?? '',
          seo_description: d.page.seo_description ?? '',
          og_image_url: d.page.og_image_url ?? '',
          nav_order: d.page.nav_order ?? null,
          nav_label: d.page.nav_label ?? '',
        });
      })
      .catch((err) => setError(err?.message || 'Could not load page.'))
      .finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, [token, pageId, isNew]);

  // Auto-suggest slug from title until user types in slug manually.
  useEffect(() => {
    if (slugTouched) return;
    if (!form.title) return;
    const kebab = form.title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    setForm((f) => ({ ...f, slug: kebab }));
  }, [form.title, slugTouched]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (saving) return;
    if (!form.title.trim() || !form.slug.trim()) {
      setError('Title and slug are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        const r = await api.createPage(token, form);
        window.location.hash = `#admin/pages/${r.page_id}`;
      } else {
        await api.updatePage(token, pageId!, form);
        // Refresh the original so subsequent saves diff against new state
        const d = await api.adminPage(token, pageId!);
        setOriginal(d.page);
      }
    } catch (err: any) {
      setError(err?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!pageId) return;
    if (!confirm('Publish this page? It will be visible to anyone visiting the marketing site.')) return;
    setSaving(true);
    try {
      await api.publishPage(token, pageId);
      const d = await api.adminPage(token, pageId);
      setOriginal(d.page);
      setForm((f) => ({ ...f, status: 'published' }));
    } catch (err: any) {
      setError(err?.message || 'Could not publish.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="p-6 text-sm text-gray-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading page…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <a href="#admin/pages" className="text-xs text-gray-500 hover:text-gray-700">← All pages</a>
          <h1 className="text-2xl font-bold text-gray-900 mt-1 flex items-center gap-2">
            <FileText className="h-6 w-6 text-uif-green-dark" />
            {isNew ? 'New page' : original?.title || 'Edit page'}
          </h1>
          {original && (
            <p className="text-sm text-gray-600 mt-0.5">
              <StatusPill status={original.status} />
              <span className="ml-3 text-xs">
                {original.published_at
                  ? `Published ${new Date(original.published_at).toLocaleDateString()}`
                  : 'Never published'}
              </span>
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setShowPreview((p) => !p)}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Eye className="h-4 w-4" />
          {showPreview ? 'Hide preview' : 'Preview'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2 text-sm flex items-start gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>{error}</div>
        </div>
      )}

      <form onSubmit={save} className="pb-20 space-y-4">
        <fieldset className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <legend className="px-2 text-xs font-semibold text-uif-green-dark uppercase tracking-wider">Basics</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Title</span>
              <input
                type="text"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Slug (URL)</span>
              <input
                type="text"
                required
                value={form.slug}
                onChange={(e) => { setSlugTouched(true); setForm({ ...form, slug: e.target.value }); }}
                disabled={saving}
                pattern="[a-z0-9-]+"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
              <p className="text-[11px] text-gray-500 mt-1">Lowercase letters, digits, and dashes only. Becomes <code>/{form.slug || 'your-slug'}</code> on upinfarms.com.</p>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Layout</span>
              <select
                value={form.layout}
                onChange={(e) => setForm({ ...form, layout: e.target.value as CmsPageLayout })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              >
                <option value="standard">Standard (header + body + footer)</option>
                <option value="landing">Landing (large hero, no nav clutter)</option>
                <option value="simple">Simple (body only, minimal chrome)</option>
              </select>
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Status</span>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as CmsPageStatus })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              >
                <option value="draft">Draft (invisible publicly)</option>
                <option value="published">Published</option>
              </select>
            </label>
          </div>
        </fieldset>

        <fieldset className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <legend className="px-2 text-xs font-semibold text-uif-green-dark uppercase tracking-wider">Hero</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Eyebrow (small label above title)</span>
              <input
                type="text"
                value={form.hero_eyebrow ?? ''}
                onChange={(e) => setForm({ ...form, hero_eyebrow: e.target.value })}
                disabled={saving}
                placeholder="e.g. About us"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Hero image URL</span>
              <input
                type="url"
                value={form.hero_image_url ?? ''}
                onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                disabled={saving}
                placeholder="https://…"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <legend className="px-2 text-xs font-semibold text-uif-green-dark uppercase tracking-wider">Body (markdown)</legend>
          <div className={'grid gap-3 ' + (showPreview ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1')}>
            <textarea
              value={form.body_md ?? ''}
              onChange={(e) => setForm({ ...form, body_md: e.target.value })}
              disabled={saving}
              rows={24}
              placeholder={'# Welcome to Up in Farms\n\nWe support Mississippi farmers with…\n\n## Our work\n\n- Workshops\n- Producer support\n- AMP grant program'}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-mono focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
            />
            {showPreview && (
              <div className="border border-gray-200 rounded-md p-4 bg-gray-50 overflow-auto" style={{ maxHeight: '600px' }}>
                <div className="prose prose-sm max-w-none">
                  <MarkdownPreview md={form.body_md ?? ''} />
                </div>
              </div>
            )}
          </div>
          <p className="text-[11px] text-gray-500">
            Supports headings, paragraphs, lists, bold/italic, links, blockquotes. Preview is approximate; the marketing site renders with the same parser at runtime.
          </p>
        </fieldset>

        <fieldset className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <legend className="px-2 text-xs font-semibold text-uif-green-dark uppercase tracking-wider">SEO</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block md:col-span-2">
              <span className="block text-xs font-medium text-gray-700 mb-1">SEO title (falls back to page title)</span>
              <input
                type="text"
                value={form.seo_title ?? ''}
                onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
                disabled={saving}
                maxLength={255}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="block text-xs font-medium text-gray-700 mb-1">Meta description</span>
              <textarea
                value={form.seo_description ?? ''}
                onChange={(e) => setForm({ ...form, seo_description: e.target.value })}
                disabled={saving}
                rows={2}
                maxLength={500}
                placeholder="150-160 characters describing the page for search results."
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="block text-xs font-medium text-gray-700 mb-1">Open Graph image URL (social preview)</span>
              <input
                type="url"
                value={form.og_image_url ?? ''}
                onChange={(e) => setForm({ ...form, og_image_url: e.target.value })}
                disabled={saving}
                placeholder="https://…"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
          </div>
        </fieldset>

        <fieldset className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <legend className="px-2 text-xs font-semibold text-uif-green-dark uppercase tracking-wider">Navigation</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Nav order (lower = earlier; blank = hide from nav)</span>
              <input
                type="number"
                min={0}
                value={form.nav_order ?? ''}
                onChange={(e) => setForm({ ...form, nav_order: e.target.value === '' ? null : Number(e.target.value) })}
                disabled={saving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-gray-700 mb-1">Nav label (shorter than title; optional)</span>
              <input
                type="text"
                value={form.nav_label ?? ''}
                onChange={(e) => setForm({ ...form, nav_label: e.target.value })}
                disabled={saving}
                maxLength={100}
                placeholder={form.title || 'About'}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-uif-green focus:ring-1 focus:ring-uif-green focus:outline-none"
              />
            </label>
          </div>
        </fieldset>

        {/* Sticky save bar */}
        <div className="sticky bottom-0 -mx-4 sm:mx-0 sm:rounded-lg bg-white border-t sm:border border-gray-200 px-4 py-3 mt-4 flex flex-wrap justify-end gap-2 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]">
          <a
            href="#admin/pages"
            className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </a>
          {!isNew && original && original.status === 'draft' && (
            <button
              type="button"
              onClick={publish}
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 text-white font-semibold px-4 py-2 text-sm hover:bg-emerald-700 disabled:bg-gray-300"
            >
              <Send className="h-4 w-4" />
              Publish
            </button>
          )}
          <button
            type="submit"
            disabled={saving || !form.title.trim() || !form.slug.trim()}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-uif-green text-white font-semibold px-5 py-2 text-sm hover:bg-uif-green-dark disabled:bg-gray-300"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {isNew ? 'Create draft' : 'Save changes'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Minimal markdown preview — same renderer shape as elsewhere in the app.
// Keeps the bundle thin (no react-markdown dependency just for previews).
// ----------------------------------------------------------------------------
function MarkdownPreview({ md }: { md: string }) {
  const html = renderMarkdownToHtml(md);
  return <div dangerouslySetInnerHTML={{ __html: html }} />;
}

function renderMarkdownToHtml(md: string): string {
  const escape = (s: string) => s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  const lines = md.split(/\r?\n/);
  const out: string[] = [];
  let inList: 'ul' | 'ol' | null = null;
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const txt = paragraph.join(' ').trim();
    if (txt) out.push(`<p>${inline(txt)}</p>`);
    paragraph = [];
  };
  const closeList = () => {
    if (inList) { out.push(`</${inList}>`); inList = null; }
  };
  const inline = (s: string) => escape(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  for (const raw of lines) {
    const line = raw.trimEnd();
    const h = line.match(/^(#{1,6})\s+(.*)$/);
    const ul = line.match(/^[-*]\s+(.*)$/);
    const ol = line.match(/^\d+\.\s+(.*)$/);

    if (line === '') {
      flushParagraph();
      closeList();
      continue;
    }
    if (h) {
      flushParagraph();
      closeList();
      const level = h[1].length;
      out.push(`<h${level}>${inline(h[2])}</h${level}>`);
      continue;
    }
    if (ul) {
      flushParagraph();
      if (inList !== 'ul') { closeList(); out.push('<ul>'); inList = 'ul'; }
      out.push(`<li>${inline(ul[1])}</li>`);
      continue;
    }
    if (ol) {
      flushParagraph();
      if (inList !== 'ol') { closeList(); out.push('<ol>'); inList = 'ol'; }
      out.push(`<li>${inline(ol[1])}</li>`);
      continue;
    }
    closeList();
    paragraph.push(line);
  }
  flushParagraph();
  closeList();
  return out.join('\n');
}
